import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService } from '../../../core/services/toast.service';
import { VendorsService } from '../../../core/services/vendors.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { authActions } from '../../../core/store/auth';
import { ApiError } from '../../../core/models/common/api-error';
import { FormFieldsetCardComponent } from '../../../shared/components/form-fieldset-card/form-fieldset-card.component';
import { User } from '../../../core/models/entities/user';
import { phoneValidator } from '../../../core/services/validation.service';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-vendor-contact-settings',
  template: `
    <app-fieldset-card
      [showSaveButton]="true"
      [saving]="saving()"
      [trackFormChanges]="form"
      (onSave)="save()"
    >
      <div title>{{ 'settings.vendor.contact.title' | transloco }}</div>
      <div description>
        {{ 'settings.vendor.contact.description' | transloco }}
      </div>

      <app-text-input
        [label]="'settings.vendor.contact.label-name' | transloco"
        name="name"
        [control]="form.controls.name"
      ></app-text-input>

      <app-text-input
        [label]="'settings.vendor.contact.label-address' | transloco"
        name="address"
        [control]="form.controls.address"
      ></app-text-input>

      <div class="grid grid-cols-1 md:grid-cols-4 md:gap-x-6">
        <app-text-input
          [label]="'settings.vendor.contact.label-zip' | transloco"
          name="zip"
          [control]="form.controls.zip"
        ></app-text-input>

        <app-text-input
          [label]="'settings.vendor.contact.label-city' | transloco"
          name="city"
          [control]="form.controls.city"
          class="col-span-3"
        ></app-text-input>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-6">
        <app-text-input
          [label]="'settings.vendor.contact.label-email' | transloco"
          name="email"
          [control]="form.controls.email"
        ></app-text-input>

        <app-phone-input
          #phoneInput
          [label]="'settings.vendor.contact.label-phone' | transloco"
          name="phone"
          [control]="form.controls.phone"
        ></app-phone-input>
      </div>
    </app-fieldset-card>
  `,
  imports: [SharedModule, SharedManagerModule],
})
export class VendorContactSettingsComponent implements OnInit, AfterViewInit {
  @Input() authUser!: User;

  @ViewChild(FormFieldsetCardComponent)
  fieldsetCard: FormFieldsetCardComponent;

  @ViewChild('phoneInput') phoneInput: ElementRef;

  private store = inject(Store);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);
  private vendorsService = inject(VendorsService);
  private formValidationService = inject(FormValidationService);

  saving = signal<boolean>(false);

  form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    address: new FormControl<string>(''),
    zip: new FormControl<string>(''),
    city: new FormControl<string>(''),
    email: new FormControl<string>(''),
    phone: new FormControl<string>(''),
    slug: new FormControl<string>('', Validators.required), // Nécessaire pour l'API
  });

  ngOnInit() {
    console.log('');
    this.form.patchValue({
      name: this.authUser.vendor.name || '',
      address: this.authUser.vendor.address || '',
      zip: this.authUser.vendor.zip || '',
      city: this.authUser.vendor.city || '',
      email: this.authUser.vendor.email || '',
      phone: this.authUser.vendor.phone || '',
      slug: this.authUser.vendor.slug || '',
    });
  }

  ngAfterViewInit() {
    this.form.controls.phone.setValidators([phoneValidator(this.phoneInput)]);
  }

  async save() {
    if (!this.formValidationService.validate(this.form)) return;

    this.saving.set(true);
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.value.name,
        slug: this.form.value.slug,
        address: this.form.value.address,
        zip: this.form.value.zip,
        city: this.form.value.city,
        email: this.form.value.email,
        phone: this.form.value.phone,
      });
      this.updateUserStore(vendor);
      this.toastService.success(
        this.translocoService.translate('settings.vendor.contact.toast-saved'),
      );

      // Réinitialiser le dirty state
      this.fieldsetCard?.resetDirtyState();
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    } finally {
      this.saving.set(false);
    }
  }

  private updateUserStore(vendor: any) {
    this.store.dispatch(
      authActions.findCurrentUserSuccess({
        user: {
          ...this.authUser,
          vendor: { ...this.authUser.vendor, ...vendor },
        },
      }),
    );
  }
}
